import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, CircularProgress } from "@mui/material";
import CashModal from "./components/modals/CashModal";
import CreditModal from "./components/modals/CreditModal";
import OrderSummary from "./components/orderSummary/CarOrderSummary";
import { roundValue } from "./utils/helpers";
import CarOrderPrint, { CarOrderPrintRef } from "./components/PrinterContent";
import { useBusinessInfo } from "./hooks/useBusinessInfo";
import { usePayment } from "./hooks/usePayment";
import { useOrder } from "./hooks/useOrder";
import OrderProductList from "./components/OrderProductList";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CommonButtons from "src/shared/components/CommonButtons";
import { toast, ToastContainer } from "react-toastify";
import { PageTitle } from "src/shared/PageTitle";

interface ModalsState {
  cash: boolean;
  credit: boolean;
  print: boolean;
}

const CarOrderDetails: React.FC = () => {
  const navigate = useNavigate();
  const business = useBusinessInfo();
  const orderHook = useOrder();
  const printComponentRef = useRef<CarOrderPrintRef | null>(null);
  const [triggerPrint, setTriggerPrint] = useState(false);

  // Add local state for modals
  const [modals, setModals] = useState<ModalsState>({
    cash: false,
    credit: false,
    print: false,
  });

  const paymentHook = usePayment(orderHook.order, orderHook.setOrder, orderHook.setStatus, orderHook.order?.orderId?.toString());

  const { order, status, loading, error, setError } = orderHook;
  const { paymentMethod, receivedPrice, setReceivedPrice, handleConfirmPayment, handlePaymentMethodChange, showPrintContent } = paymentHook;

  // Get the latest payment for printing - transform to expected format
  const latestPayment = React.useMemo(() => {
    if (!order?.paymentHistory?.length) return undefined;

    const lastPayment = order.paymentHistory[order.paymentHistory.length - 1];

    return {
      receivedAmount: lastPayment.receivedAmount || 0,
      paymentMethod: lastPayment.paymentMethod || "N/A",
      newReceivedAmount: lastPayment.receivedAmount || 0,
    };
  }, [order]);

  const handlePrint = () => {
    // Trigger print from the CarOrderPrint component
    if (printComponentRef.current) {
      printComponentRef.current.triggerPrint();
      toast.success("Print dialog opened!");
    }
  };

  const handleCloseModal = () => {
    setModals({ cash: false, credit: false, print: false });
  };

  // Wrapper functions to handle payment and then close modal
  const handleCashPayment = async () => {
    await handleConfirmPayment(paymentMethod);
    handleCloseModal(); // Close modal after payment
  };

  const handleCreditPayment = async () => {
    await handleConfirmPayment(paymentMethod);
    handleCloseModal(); // Close modal after payment
  };

  const cashModalProps = {
    order,
    paymentMethod,
    receivedPrice,
    setReceivedPrice,
    handleCloseModal,
    handleConfirmPayment: handleCashPayment,
  };

  const creditModalProps = {
    order,
    paymentMethod,
    handleCloseModal,
    handleConfirmPayment: handleCreditPayment,
  };

  const summaryProps = {
    order,
    status,
    setStatus: orderHook.setStatus,
    handleStatusChange: orderHook.handleStatusChange,
    paymentMethod,
    handlePaymentMethodChange,
    roundValue,
    cancelOrder: orderHook.cancelOrder,
    isCancelDisabled: orderHook.isCancelDisabled,
    handlePrint,
    openCashModal: () => setModals((prev) => ({ ...prev, cash: true })),
    openCreditModal: () => setModals((prev) => ({ ...prev, credit: true })),
  };

  return (
    <div className="h-[88vh] bg-gray-50 p-5 overflow-hidden">
      <PageTitle title="Order-Details" />
      <div className="h-full flex flex-col">
        {/* Compact Header */}
        <div className="mb-3 flex items-center justify-between flex-shrink-0">
          <CommonButtons label="BACK" onClick={() => navigate(-1)} variant="secondary" icon={KeyboardReturnIcon} className="text-sm" />
          <h1 className="text-xl font-bold text-gray-800">Order Details</h1>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-full">
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[60%_40%] gap-3 min-h-0">
            {/* Product List - 60% width */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden h-full">
              <OrderProductList order={order} />
            </div>

            {/* Order Summary - 40% width */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden h-full">
              <OrderSummary {...summaryProps} />
            </div>
          </div>
        )}

        {/* Modals and Toast */}
        {modals.cash && <CashModal {...cashModalProps} />}
        {modals.credit && <CreditModal {...creditModalProps} />}
        {error && <Snackbar open autoHideDuration={6000} onClose={() => setError(null)} message={error} />}

        {/* Hidden CarOrderPrint Component - Used for printing */}
        {order && (
          <div className="hidden">
            <CarOrderPrint ref={printComponentRef} order={order} latestPayment={latestPayment} onPrintComplete={() => {}} triggerPrint={showPrintContent} />
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      </div>
    </div>
  );
};

export default CarOrderDetails;
